<script lang="ts" setup>
import type { PropType } from 'vue'
import { ElTag } from 'element-plus'
import { debounce } from 'lodash-es'

const props = defineProps({
  modelValue: { type: Array as PropType<string[]>, required: true },
  placeholder: {
    type: String,
    default: 'john.do@bigtrucksco.com, other.user@bigtrucksco.com...',
  },
  helpText: {
    type: String,
    default: 'Emails can be separated by commas, new lines or spaces',
  },
  extraMessages: { type: Array as PropType<string[]>, default: () => [] },
  minRows: { type: Number, default: 5 },
})

const emit = defineEmits<{ (e: 'update:modelValue', value: string[]): void }>()

const rawEmails = ref('')
const emailAddresses = useVModel(props, 'modelValue', emit)
const isInitializing = ref(true)

// Initialize the text field with existing emails when component mounts
onMounted(() => {
  if (emailAddresses.value.length > 0) {
    rawEmails.value = emailAddresses.value.join(', ')
  }
  // Allow normal operation after initial load
  nextTick(() => {
    isInitializing.value = false
  })
})

// More robust email validation function
const isValidEmail = (email: string): boolean => {
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return emailRegex.test(email)
}

const parsedEmails = computed(() => {
  if (!rawEmails.value.trim()) return []

  return rawEmails.value
    .split(/[\s,\n<>;]+/)
    .map((s: string) => s.trim().toLowerCase())
    .filter((s: string) => s && isValidEmail(s))
})

// Update the model value in real-time as user types
const updateEmails = debounce(() => {
  if (!isInitializing.value) {
    emailAddresses.value = [...parsedEmails.value]
  }
}, 500)

// Watch for changes in the raw input and update emails automatically
watch(rawEmails, () => {
  updateEmails()
})

// Only watch for external changes during initialization
watch(
  emailAddresses,
  (newEmails) => {
    if (isInitializing.value && newEmails.length > 0) {
      rawEmails.value = newEmails.join(', ')
    }
  },
  { immediate: true }
)
</script>

<template>
  <div class="email-addresses-input">
    <el-input
      v-model="rawEmails"
      type="textarea"
      :placeholder="placeholder"
      :autosize="{ minRows }"
      class="my-2"
    />

    <el-alert :title="helpText" type="info" show-icon :closable="false" />

    <template v-if="extraMessages && extraMessages.length">
      <div v-for="message in extraMessages" :key="message" class="mt-2">
        <el-alert :title="message" type="info" show-icon :closable="false" />
      </div>
    </template>

    <h3 class="text-md mt-2">Parsed emails:</h3>
    <div>
      <ElTag v-if="parsedEmails.length === 0" type="warning" class="mr-2 my-1">
        No emails found, enter some above
      </ElTag>
      <ElTag
        v-for="email in parsedEmails"
        :key="email"
        type="success"
        class="mr-2 my-1"
      >
        {{ email }}
      </ElTag>
    </div>
  </div>
</template>

<style scoped>
.email-addresses-input {
  width: 100%;
}
</style>
